import { useSearchQueryParam } from '@piaas/piaas-ui-toolkit/hooks';
import { useState, useMemo, type FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import Auth from '../../../api/auth';
import GitlabWikiHelpButton from '../../../components/GitlabWikiHelpButton';
import Table from '../../../components/Table';
import { roleMessageId } from '../../../helpers/lookups';
import useTableApiCall from '../../../hooks/useTableApiCall';
import OrganizationBreadCrumb from '../OrganizationBreadCrumb';

import type { ErrorType } from '@/types';
import type { User as UserData } from '@/types/user';

const usersGuideSlug = 'user-guide/organizations/users';

const Users: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { organizationId } = useParams<{ organizationId: string }>();
  const resolvedOrganizationId = organizationId ?? '';

  const [rows, setRows] = useState<UserData[]>([]);

  const endpointParams = useMemo(
    () => ({ organizationId: resolvedOrganizationId, pagination: true }),
    [resolvedOrganizationId]
  );
  const [initialSearch] = useSearchQueryParam('search');
  const { setCurrentPage, itemsPerPage, setItemsPerPage, ...tableData } = useTableApiCall(
    Auth.fetchV2Users,
    {
      isPaginated: true,
      setRows: setRows as (rows: unknown[]) => void,
      searchColumns: ['email', 'roles'],
      endpointParams,
      initialFilter: initialSearch,
      searchParamKey: 'search',
      initialItemsPerPage: 5,
    }
  );

  const fields = [
    { title: { id: 'email' }, id: 'email' },
    { title: { id: 'role' }, id: 'roles' },
  ];

  const transformedRows = rows.map(user => ({
    key: user.uuid,
    data: [
      { field: 'email', value: user.email },
      { field: 'roles', value: intl.formatMessage({ id: roleMessageId(user.role) }) },
    ],
    onRowClick: () => {
      if (!resolvedOrganizationId) {
        return;
      }
      navigate(`/governance/organizations/${resolvedOrganizationId}/users/${user.uuid}`);
    },
  }));

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  return (
    <>
      <OrganizationBreadCrumb />
      <Table
        header={intl.formatMessage({ id: 'users' })}
        fields={fields}
        rows={transformedRows}
        headerActionSlot={{
          content: (
            <GitlabWikiHelpButton
              slug={usersGuideSlug}
              tooltipMessageId='usersGuide'
              tooltipDefaultMessage='Users guide'
              modalTitleMessageId='usersGuide'
              modalTitleDefaultMessage='Users guide'
              buttonAriaLabel={intl.formatMessage({ id: 'usersGuideAriaLabel' })}
              buttonSx={{ mt: 0 }}
            />
          ),
        }}
        pagination
        {...tableData}
        pageSize={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        error={tableData.error as ErrorType}
        setFilter={tableData.setFilter as (filter: unknown) => void}
        onPageChange={handlePageChange}

        advancedSearch={false}
      />
    </>
  );
};

export default Users;
